import { Container, Typography, Box, Divider, Link as JoyLink } from '@mui/joy';
import { ArrowLeft } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import {
    Stack,
    AccordionSummary,
    Sheet,
} from '@mui/joy';
import { CaretDown } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import faq from '../../../data/faq/faq';
import { useState } from 'react';
import { useSeoHead } from '../../../hooks/service/useSeoHead';

const SITE_URL = 'https://aquarium.org.ru';

const FAQ_TITLE = 'Ответы на вопросы — Аквариум';
const FAQ_DESCRIPTION = 'Часто задаваемые вопросы об Аквариуме: что это, регистрация и вход, безопасность, функции мессенджера и публикаций. Собрали ответы в одном месте.';

// FAQPage-разметка для расширенных сниппетов в поиске (теги из ответов убираем).
const FAQ_JSONLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq
        .flatMap((section) => section.items)
        .map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.a.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
            },
        })),
};

function FAQAccordion() {
    const [expanded, setExpanded] = useState(false);

    const toggleAccordion = () => setExpanded((prev) => !prev);

    return (
        <Sheet
            variant="plain"
            sx={{
                px: 2,
                py: 1,
                borderRadius: '32px',
            }}
        >
            <AccordionSummary
                onClick={toggleAccordion}
                indicator={null}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '& button': {
                        borderRadius: '50px',
                        px: 2,
                        py: 1,
                        mx: -1
                    },
                }}
            >
                <Typography level="h4">Вопросы и разделы</Typography>
                <motion.div
                    animate={{ rotate: expanded ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <CaretDown size={20} />
                </motion.div>
            </AccordionSummary>
            <motion.div
                initial={false}
                animate={{ height: expanded ? 'auto' : 0 }}
                transition={{
                    type: 'spring',
                    stiffness: 350,
                    damping: 30,
                    mass: 1,
                }}
                style={{ overflow: 'hidden' }}
            >
                <Stack spacing={1} sx={{ pt: 2, pb: 4, px: 2 }}>
                    {faq.map((section) => {
                        const sectionId = slugify(section.title);
                        return (
                            <Stack key={sectionId} spacing={0.5}>
                                <JoyLink level="title-lg" sx={{ mb: '8px !important', mt: '4px !important' }} href={`#${sectionId}`}>{section.title}</JoyLink>
                                <Stack pl={2}>
                                    {section.items.map((item) => {
                                        const qId = slugify(item.q);
                                        return (
                                            <JoyLink
                                                key={qId}
                                                level="title-md"
                                                href={`#${qId}`}
                                                sx={{ py: '4px' }}
                                            >
                                                {item.q}
                                            </JoyLink>
                                        );
                                    })}
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
            </motion.div>
        </Sheet>
    );
}

function FAQList() {
    return faq.map((section, i) => {
        const sectionId = slugify(section.title);
        return (
            <Box key={sectionId}>
                <Typography id={sectionId} level="h1">
                    {section.title}
                </Typography>

                <Box>
                    {section.items.map((item) => {
                        const qId = slugify(item?.q);
                        return (
                            <Box key={qId}>
                                <Typography id={qId} level="title-lg" sx={{ mb: 2 }}>
                                    {item?.q}
                                </Typography>
                                <Typography
                                    sx={{
                                        mb: 4,
                                        '& ul': { m: '0', pl: '24px' },
                                    }}
                                >
                                    <span dangerouslySetInnerHTML={{ __html: item?.a }} />
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {i < faq.length - 1 && <Divider sx={{ my: 5 }} />}
            </Box>
        );
    });
}

function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^a-zа-я0-9]+/gi, '-')
        .replace(/(^-|-$)/g, '');
}


export default function PageFAQ() {
    useSeoHead({
        title: FAQ_TITLE,
        description: FAQ_DESCRIPTION,
        canonical: `${SITE_URL}/faq`,
        image: `${SITE_URL}/web-app-manifest-512x512.png`,
        og: { 'og:type': 'website', 'og:site_name': 'Аквариум', 'og:locale': 'ru_RU' },
        twitter: { 'twitter:card': 'summary_large_image' },
        jsonLd: FAQ_JSONLD,
    });

    return (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography
                    startDecorator={<ArrowLeft size={20} />}
                    level="body-md"
                    sx={{ mb: 4, display: 'inline-flex', alignItems: 'center', gap: .5, '&:hover': { color: 'primary.plainColor' } }}
                >
                    На главную
                </Typography>
            </Link>

            <Typography level="h1" sx={{ mb: 1 }}>
                Ответы на вопросы
            </Typography>
            <Typography sx={{ mb: 5, color: 'text.tertiary' }}>
                Собрали в одном месте ответы на часто задаваемые вопросы.
            </Typography>

            <Box sx={{ mb: 2 }}>
                <FAQAccordion />
            </Box>

            <Box sx={{
                bgcolor: 'background.surface',
                p: { xs: 3, md: 6 },
                borderRadius: '24px',
                lineHeight: 1.85,
                wordBreak: 'break-word'
            }}>
                <FAQList />
            </Box>
        </Container>
    );
}
